import React, { useState } from "react";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";

export default function HumanLinePage() {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({
    email: "",
    areas_of_interest: [],
    privacy_consent: false,
  });
  const [loading, setLoading] = useState(false);

  const toggleArea = (a) => {
    setForm((f) => ({
      ...f,
      areas_of_interest: f.areas_of_interest.includes(a)
        ? f.areas_of_interest.filter((x) => x !== a)
        : [...f.areas_of_interest, a],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_consent) {
      toast.error("Consenso privacy obbligatorio.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/human-line/notify", form);
      toast.success(data.message);
      setForm({ email: "", areas_of_interest: [], privacy_consent: false });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="human-line-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-16 lg:pt-24 pb-16">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-editorial text-human mb-4 flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-human" />
              {t("human.overline")}
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-brand leading-[0.98]">
              {t("human.title")}
            </h1>
            <p className="mt-8 text-lg text-muted-ink leading-relaxed">{t("human.sub")}</p>
          </div>
        </div>
      </section>

      {/* Manifesto */}
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="text-xs uppercase tracking-editorial text-muted-ink mb-3">Manifesto</div>
            <h2 className="font-display text-4xl text-brand leading-tight">
              {t("human.manifesto_title")}
            </h2>
          </div>
          <div className="md:col-span-7">
            <p className="text-lg text-muted-ink leading-relaxed">{t("human.manifesto_txt")}</p>
            <div className="mt-8 grid grid-cols-2 gap-4">
              <div className="border border-soft p-5 bg-white">
                <div className="text-xs uppercase tracking-editorial text-human mb-1">{lang === "en" ? "Transparency" : "Trasparenza"}</div>
                <p className="text-sm text-brand">{lang === "en" ? "Clear ingredients, cautious claims, responsible communication." : "Ingredienti chiari, claim prudenti, comunicazione responsabile."}</p>
              </div>
              <div className="border border-soft p-5 bg-white">
                <div className="text-xs uppercase tracking-editorial text-human mb-1">{lang === "en" ? "Research" : "Ricerca"}</div>
                <p className="text-sm text-brand">{lang === "en" ? "Formulations developed from available literature." : "Formulazioni studiate a partire dalla letteratura disponibile."}</p>
              </div>
              <div className="border border-soft p-5 bg-white">
                <div className="text-xs uppercase tracking-editorial text-human mb-1">{lang === "en" ? "Quality" : "Qualità"}</div>
                <p className="text-sm text-brand">{lang === "en" ? "Italian production, traced raw materials." : "Produzione italiana, materie prime tracciate."}</p>
              </div>
              <div className="border border-soft p-5 bg-white">
                <div className="text-xs uppercase tracking-editorial text-human mb-1">{lang === "en" ? "Simplicity" : "Semplicità"}</div>
                <p className="text-sm text-brand">{lang === "en" ? "Clear formulas, understandable instructions." : "Formule chiare, modalità d'uso comprensibili."}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Notify form */}
      <section className="bg-cream-warm py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="text-xs uppercase tracking-editorial text-human mb-3">Ricevi gli aggiornamenti</div>
            <h2 className="font-display text-4xl text-brand leading-tight">
              {t("human.notify_title")}
            </h2>
            <p className="mt-4 text-muted-ink max-w-md">{t("human.notify_sub")}</p>
          </div>
          <form onSubmit={submit} className="md:col-span-7 space-y-5" data-testid="human-notify-form">
            <Input
              type="email"
              required
              placeholder={t("common.email")}
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="rounded-none border-soft bg-white"
              data-testid="human-notify-email"
            />
            <div>
              <label className="block text-xs uppercase tracking-editorial text-muted-ink mb-3">
                {t("human.areas_label")}
              </label>
              <div className="flex flex-wrap gap-2">
                {t("human.areas").map((a) => (
                  <button
                    type="button"
                    key={a}
                    onClick={() => toggleArea(a)}
                    className={`px-4 py-2 border text-sm transition-colors ${
                      form.areas_of_interest.includes(a)
                        ? "border-human bg-human text-white"
                        : "border-soft text-brand hover:bg-white"
                    }`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>
            <label className="flex items-start gap-2 text-sm text-muted-ink cursor-pointer">
              <input
                type="checkbox"
                required
                checked={form.privacy_consent}
                onChange={(e) => setForm((f) => ({ ...f, privacy_consent: e.target.checked }))}
                className="mt-1"
                data-testid="human-notify-privacy"
              />
              <span>{t("common.privacy_consent")}</span>
            </label>
            <button
              type="submit"
              disabled={loading}
              data-testid="human-notify-submit"
              className="rounded-full bg-human text-white px-8 py-3 text-sm hover:brightness-95 disabled:opacity-50 transition"
            >
              {loading ? t("common.sending") : (lang === "en" ? "Notify me" : "Iscrivimi")}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
